import EmptyState from "../../../shared/components/EmptyState";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import AppLayout from "../../../shared/components/AppLayout";
import LoadingPage from "../../../shared/components/LoadingPage";
import PageSizeSelector, {
  resolvePageSizeLimit,
  useStoredPageSize,
} from "../../../shared/components/PageSizeSelector";
import SearchInput from "../../../shared/components/SearchInput";
import TnpOrganizerLogo, {
  isTnpOrganizer,
} from "../../../shared/components/TnpOrganizerLogo";
import UserAvatar from "../../../shared/components/UserAvatar";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { useUserLocation } from "../../../shared/location/userLocation";
import type { UserLocation } from "../../../shared/location/userLocation";
import { formatEventDateRangeLabel } from "../../../shared/utils/dateTime";
import { normalizeSearchText } from "../../../shared/utils/searchText";
import { listPublicChallenges } from "../services/challengeAdminService";
import {
  getPublicOrganizerVisualIdentity,
  listOrganizers,
} from "../../auth/shared/services/authService";
import type { OrganizerVisualIdentitySettings } from "../../auth/shared/types/auth";
import {
  ChallengeHistoryItem,
  ChallengeStatus,
  ChallengeType,
} from "../types/challengeAdmin";
import { resolveChallengeCoverImage } from "../utils/challengeCoverImage";
import { getPocoTotalPlayersCapacity } from "../utils/pocoSchedule";
import { organizerIdentityStyle } from "../../../shared/utils/organizerVisualIdentity";
import useOrganizerPageVisualIdentity from "../../../shared/hooks/useOrganizerPageVisualIdentity";
import { useSiteContent } from "../../home/shared/hooks/useSiteContent";
import "../../../shared/components/ContentCard.css";
import { useAuth } from "../../auth/shared/hooks/useAuth";
import "./ChallengesPage.css";

function typeLabel(type: ChallengeType) {
  if (type === "challenge") return siteTexts.challenges.modes.challenge.title;
  if (type === "super8") return siteTexts.challenges.modes.super8.title;
  if (type === "poco") return siteTexts.challenges.modes.poco.title;
  return siteTexts.challenges.modes.tournament.title;
}

function categoryLabel(code?: string | null) {
  const raw = String(code || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, "");
  if (!raw) return "";
  if (raw === "7a" || raw === "7") return siteTexts.common.category.c7a;
  if (raw === "6a" || raw === "6") return siteTexts.common.category.c6a;
  if (raw === "5a" || raw === "5") return siteTexts.common.category.c5a;
  if (raw === "4a" || raw === "4") return siteTexts.common.category.c4a;
  if (raw === "3a" || raw === "3") return siteTexts.common.category.c3a;
  if (raw === "2a" || raw === "2") return siteTexts.common.category.c2a;
  if (raw === "open" || raw === "aberta" || raw === "aberto") {
    return siteTexts.common.category.open;
  }
  if (raw === "free" || raw === "livre" || raw === "social") {
    return siteTexts.common.category.free;
  }
  return String(code || "").trim();
}

function registrationCategoriesSummary(item: ChallengeHistoryItem) {
  const categories = Array.isArray(item.registrationCategories)
    ? item.registrationCategories
    : [];
  if (categories.length === 0) return "";

  const labels = Array.from(
    new Set(
      categories
        .map((category) => String(category?.categoryName || "").trim())
        .filter(Boolean),
    ),
  );

  return labels.join(" • ");
}

function categorySummary(item: ChallengeHistoryItem) {
  const registrationSummary = registrationCategoriesSummary(item);
  if (registrationSummary) return registrationSummary;

  const gender = item.categoryGender;
  const category = categoryLabel(item.category);
  const categoryMale = categoryLabel(item.categoryMale);
  const categoryFemale = categoryLabel(item.categoryFemale);

  if (gender === "mixed" || gender === "open") {
    const genderLabel =
      gender === "open"
        ? siteTexts.common.challengeGender.open
        : siteTexts.common.challengeGender.mixed;
    if (categoryMale && categoryFemale) {
      if (categoryMale === categoryFemale) {
        return `${genderLabel} • ${categoryMale}`;
      }
      return `${genderLabel} • ${categoryMale} / ${categoryFemale}`;
    }
    if (category)
      return `${genderLabel} • ${category}`;
    return genderLabel;
  }
  if (gender === "male") {
    const value = category || categoryMale;
    return value
      ? `${siteTexts.common.challengeGender.male} • ${value}`
      : siteTexts.common.challengeGender.male;
  }
  if (gender === "female") {
    const value = category || categoryFemale;
    return value
      ? `${siteTexts.common.challengeGender.female} • ${value}`
      : siteTexts.common.challengeGender.female;
  }
  if (category) return category;
  if (categoryMale && categoryFemale) {
    if (categoryMale === categoryFemale) return categoryMale;
    return `${categoryMale} / ${categoryFemale}`;
  }
  return (
    categoryMale || categoryFemale || siteTexts.common.category.notInformed
  );
}

function minParticipantsByType(item: ChallengeHistoryItem) {
  const slotLimit = Number(item.slotLimit || 0);
  if (
    item.challengeType === "tournament" &&
    Number.isFinite(slotLimit) &&
    slotLimit > 0
  ) {
    return Math.floor(slotLimit) * 2;
  }
  if (item.challengeType === "super8") return 8;
  if (item.challengeType === "poco") {
    return getPocoTotalPlayersCapacity({
      courtsCount: item.courtsCount,
      playersCount: item.playersCount,
      registrationCategories: item.registrationCategories,
    });
  }
  return 12;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function resolvePublicStatusBadge(statusRaw: string) {
  const statusLabels = siteTexts.challenges.publicList.filters;
  const status = String(statusRaw || "")
    .trim()
    .toLowerCase();
  if (status === "pending")
    return { label: statusLabels.pending, tone: "pending" };
  if (status === "started")
    return { label: statusLabels.started, tone: "started" };
  if (status === "finished")
    return { label: statusLabels.finished, tone: "finished" };
  if (status === "canceled" || status === "cancelado") {
    return {
      label: siteTexts.challenges.publicDetail.statuses.canceled,
      tone: "canceled",
    };
  }
  return {
    label: siteTexts.challenges.publicDetail.labels.toBeDefined,
    tone: "pending",
  };
}

type ChallengeListViewItem = {
  source: ChallengeHistoryItem;
  viewKey: string;
  viewGroup: "A" | "B" | null;
  title: string;
  detailHref: string;
  displayAt: string;
  displayEndAt: string;
  distanceKm: number | null;
};

function haversineKm(a: UserLocation, b: UserLocation) {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const earthKm = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * earthKm * Math.asin(Math.sqrt(h));
}

function challengeDistanceKm(
  item: ChallengeHistoryItem,
  userLocation: UserLocation | null,
) {
  if (!userLocation) return null;
  const latitude = Number(item.latitude);
  const longitude = Number(item.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  return haversineKm(userLocation, { latitude, longitude });
}

function addHoursToIsoString(value: string, hoursToAdd: number) {
  const parsed = new Date(String(value || ""));
  if (Number.isNaN(parsed.getTime())) return String(value || "");
  parsed.setHours(parsed.getHours() + hoursToAdd);
  return parsed.toISOString();
}

function isCombinedSuper8(item: ChallengeHistoryItem) {
  if (item.challengeType !== "super8") return false;
  const matchesCount = Number(item.matchesCount || 0);
  // Started/finished Super8 is combined only when two full schedules exist.
  if (item.status !== "pending") return matchesCount === 28;
  return false;
}

type ChallengesPageProps = {
  forcedOfficialFilter?: "official" | "organizer";
  initialOfficialFilter?: "official" | "organizer";
  forcedOrganizerUserId?: number;
  forcedTypeFilter?: ChallengeType;
  routeBasePath?: "desafios" | "torneios";
};

function categoryTone(item: ChallengeHistoryItem) {
  if (item.categoryGender === "male") return "male";
  if (item.categoryGender === "female") return "female";
  if (item.categoryGender === "mixed") return "mixed";
  if (item.categoryGender === "open") return "free";
  return "neutral";
}

function remainingSlots(item: ChallengeHistoryItem) {
  return Math.max(
    0,
    minParticipantsByType(item) - Number(item.participantsCount || 0),
  );
}

function tournamentFormatLabel(item: ChallengeHistoryItem) {
  const format = String((item as { format?: unknown }).format || "").trim();
  if (format) return format;

  return item.registrationType === "fixed_pair"
    ? "Duplas fixas + Mata-mata"
    : "Fase de grupos + Mata-mata";
}

export default function ChallengesPage({
  forcedOfficialFilter,
  initialOfficialFilter,
  forcedOrganizerUserId,
  forcedTypeFilter,
  routeBasePath = "desafios",
}: ChallengesPageProps = {}) {
  const { content } = useSiteContent();
  const userLocation = useUserLocation();
  const hasForcedOrganizerUserId =
    Number.isFinite(forcedOrganizerUserId) && forcedOrganizerUserId > 0;
  const showOrganizerSelectorOnly =
    forcedOfficialFilter === "organizer" && !hasForcedOrganizerUserId;
  const { token } = useAuth();
  const [items, setItems] = useState<ChallengeHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [officialFilter, setOfficialFilter] = useState<
    "all" | "official" | "organizer"
  >(forcedOfficialFilter || initialOfficialFilter || "all");
  const [organizerFilterValue, setOrganizerFilterValue] = useState(
    forcedOrganizerUserId ? String(forcedOrganizerUserId) : "",
  );
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<
    ChallengeStatus[]
  >([]);
  const [selectedTypeFilters, setSelectedTypeFilters] = useState<
    ChallengeType[]
  >(forcedTypeFilter ? [forcedTypeFilter] : []);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [organizerDirectory, setOrganizerDirectory] = useState<
    Array<{ id: number; label: string }>
  >([]);
  const [organizerVisualIdentity, setOrganizerVisualIdentity] =
    useState<OrganizerVisualIdentitySettings | null>(null);
  useOrganizerPageVisualIdentity(
    organizerVisualIdentity,
    hasForcedOrganizerUserId,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPageValue, setItemsPerPageValue] = useStoredPageSize(
    routeBasePath === "torneios"
      ? "tnp_page_size_public_torneios"
      : "tnp_page_size_public_desafios",
  );
  const refreshingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!hasForcedOrganizerUserId || !forcedOrganizerUserId) {
      setOrganizerVisualIdentity(null);
      return;
    }
    getPublicOrganizerVisualIdentity(forcedOrganizerUserId)
      .then((identity) => {
        if (active) setOrganizerVisualIdentity(identity);
      })
      .catch(() => {
        if (active) setOrganizerVisualIdentity(null);
      });
    return () => {
      active = false;
    };
  }, [forcedOrganizerUserId, hasForcedOrganizerUserId]);

  useEffect(() => {
    let cancelled = false;
    listOrganizers()
      .then((response) => {
        if (cancelled) return;
        const next = (response.items || [])
          .map((item) => ({
            id: Number(item.id || 0),
            label: String(item.name || "").trim(),
          }))
          .filter(
            (item) => Number.isFinite(item.id) && item.id > 0 && item.label,
          )
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
        setOrganizerDirectory(next);
      })
      .catch(() => {
        if (!cancelled) setOrganizerDirectory([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (forcedOfficialFilter) {
      setOfficialFilter(forcedOfficialFilter);
      return;
    }
    if (initialOfficialFilter) {
      setOfficialFilter(initialOfficialFilter);
      setOrganizerFilterValue("");
    }
    if (hasForcedOrganizerUserId) {
      setOrganizerFilterValue(String(forcedOrganizerUserId));
    }
  }, [
    forcedOfficialFilter,
    forcedOrganizerUserId,
    hasForcedOrganizerUserId,
    initialOfficialFilter,
  ]);

  useEffect(() => {
    if (forcedTypeFilter) {
      setSelectedTypeFilters([forcedTypeFilter]);
      return;
    }
    setSelectedTypeFilters([]);
  }, [forcedTypeFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(normalizeSearchText(searchInput));
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const effectiveOfficialFilter = forcedOfficialFilter || officialFilter;
  const effectiveOrganizerFilterValue = hasForcedOrganizerUserId
    ? String(forcedOrganizerUserId)
    : organizerFilterValue;
  const allowedTypeFilters = useMemo<ChallengeType[]>(
    () =>
      forcedTypeFilter ? [forcedTypeFilter] : ["challenge", "super8", "poco"],
    [forcedTypeFilter],
  );

  const fetchChallenges = useCallback(
    async ({ showLoading }: { showLoading: boolean }) => {
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      if (showLoading && mountedRef.current) setLoading(true);
      try {
        if (mountedRef.current) setLoadError("");
        const data = await listPublicChallenges(
          {
            official:
              effectiveOfficialFilter === "all"
                ? "all"
                : effectiveOfficialFilter === "official",
            organizerUserId:
              effectiveOfficialFilter === "official" ||
              !effectiveOrganizerFilterValue
                ? undefined
                : Number(effectiveOrganizerFilterValue),
            types: allowedTypeFilters,
          },
          token,
        );
        if (!mountedRef.current) return;
        setItems(data);
      } catch (_err) {
        if (!mountedRef.current) return;
        if (showLoading) {
          setItems([]);
          setLoadError(
            _err instanceof Error
              ? _err.message
              : siteTexts.challenges.publicList.labels.loadErrorFallback,
          );
        }
      } finally {
        if (mountedRef.current && showLoading) setLoading(false);
        refreshingRef.current = false;
      }
    },
    [
      allowedTypeFilters,
      effectiveOfficialFilter,
      effectiveOrganizerFilterValue,
      token,
    ],
  );

  useEffect(() => {
    void fetchChallenges({ showLoading: true });
  }, [fetchChallenges]);

  const hasLiveChallenge = useMemo(
    () => items.some((item) => item.status === "started"),
    [items],
  );

  useEffect(() => {
    if (!hasLiveChallenge) return;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void fetchChallenges({ showLoading: false });
    }, 8000);
    return () => window.clearInterval(intervalId);
  }, [fetchChallenges, hasLiveChallenge]);

  const filteredItems = items.filter((item) => {
    const matchesAllowedType = allowedTypeFilters.includes(item.challengeType);
    const baseTitle = String(item.challengeName || item.groupName || "")
      .trim();
    const organizer = String(item.organizerName || "")
      .trim();
    const location = String(item.address || "")
      .trim();
    const normalizedSearchQuery = normalizeSearchText(searchQuery);
    const matchesSearch =
      !normalizedSearchQuery ||
      normalizeSearchText(baseTitle).includes(normalizedSearchQuery) ||
      normalizeSearchText(organizer).includes(normalizedSearchQuery) ||
      normalizeSearchText(location).includes(normalizedSearchQuery);
    const matchesStatus =
      selectedStatusFilters.length === 0 ||
      selectedStatusFilters.includes(item.status);
    const matchesType =
      selectedTypeFilters.length === 0 ||
      selectedTypeFilters.includes(item.challengeType);
    return matchesAllowedType && matchesSearch && matchesStatus && matchesType;
  });
  const visibleItems = useMemo<ChallengeListViewItem[]>(() => {
    const mappedItems = filteredItems.flatMap((item) => {
      const baseTitle = item.challengeName || item.groupName;
      const distanceKm = challengeDistanceKm(item, userLocation);
      if (!isCombinedSuper8(item)) {
        return [
          {
            source: item,
            viewKey: `challenge-${item.id}`,
            viewGroup: null,
            title: baseTitle,
            detailHref: `#/${routeBasePath}/detalhe/${item.id}`,
            displayAt: item.scheduledAt || item.createdAt,
            displayEndAt: item.scheduledEndAt || "",
            distanceKm,
          },
        ];
      }
      return (["A", "B"] as const).map((group) => ({
        source: item,
        viewKey: `challenge-${item.id}-group-${group}`,
        viewGroup: group,
        title: `${baseTitle} • ${siteTexts.challenges.publicList.labels.groupPrefix} ${group}`,
        detailHref: `#/${routeBasePath}/detalhe/${item.id}?group=${group}`,
        displayAt:
          group === "B"
            ? addHoursToIsoString(item.scheduledAt || item.createdAt, 2)
            : item.scheduledAt || item.createdAt,
        displayEndAt:
          group === "B" && item.scheduledEndAt
            ? addHoursToIsoString(item.scheduledEndAt, 2)
            : item.scheduledEndAt || "",
        distanceKm,
      }));
    });

    if (forcedTypeFilter !== "tournament" || !userLocation) return mappedItems;

    return mappedItems
      .map((item, index) => ({ item, index }))
      .sort((left, right) => {
        const leftDistance = Number.isFinite(left.item.distanceKm)
          ? Number(left.item.distanceKm)
          : Number.POSITIVE_INFINITY;
        const rightDistance = Number.isFinite(right.item.distanceKm)
          ? Number(right.item.distanceKm)
          : Number.POSITIVE_INFINITY;
        if (leftDistance !== rightDistance) return leftDistance - rightDistance;
        return left.index - right.index;
      })
      .map(({ item }) => item);
  }, [filteredItems, forcedTypeFilter, routeBasePath, userLocation]);

  const organizerOptions = useMemo(() => {
    const map = new Map<number, string>();
    organizerDirectory.forEach((item) => {
      map.set(item.id, item.label);
    });
    items.forEach((item) => {
      const organizerId = Number(item.organizerUserId || 0);
      if (!Number.isFinite(organizerId) || organizerId <= 0) return;
      const label =
        String(item.organizerName || "").trim() ||
        `${siteTexts.challenges.publicList.labels.organizerByIdPrefix}${organizerId}`;
      map.set(organizerId, label);
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [items, organizerDirectory]);
  const challengesPageTitle = useMemo(() => {
    if (forcedTypeFilter === "tournament") {
      return siteTexts.challenges.publicList.titles.tournaments;
    }
    if (effectiveOfficialFilter !== "organizer")
      return siteTexts.challenges.publicList.title;
    const organizerId = Number(effectiveOrganizerFilterValue || 0);
    if (!Number.isFinite(organizerId) || organizerId <= 0) {
      return siteTexts.challenges.publicList.titles.organizers;
    }
    const organizerName =
      organizerOptions.find((item) => item.id === organizerId)?.label || "";
    if (organizerName) {
      return `${siteTexts.challenges.publicList.titles.organizerPrefix} ${organizerName}`;
    }
    return `${siteTexts.challenges.publicList.titles.organizerIdPrefix}${organizerId}`;
  }, [
    effectiveOfficialFilter,
    effectiveOrganizerFilterValue,
    forcedTypeFilter,
    organizerOptions,
  ]);

  const totalItems = visibleItems.length;
  const itemsPerPage = resolvePageSizeLimit(itemsPerPageValue, totalItems);
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(totalItems, currentPage * itemsPerPage);
  const paginatedItems = useMemo(() => {
    if (itemsPerPageValue === "all") return visibleItems;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return visibleItems.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, itemsPerPage, itemsPerPageValue, visibleItems]);

  const paginationTokens = useMemo(() => {
    const tokens: Array<number | "ellipsis-left" | "ellipsis-right"> = [];
    if (totalPages <= 7) {
      for (let page = 1; page <= totalPages; page += 1) tokens.push(page);
      return tokens;
    }
    tokens.push(1);
    if (currentPage > 3) tokens.push("ellipsis-left");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) tokens.push(page);
    if (currentPage < totalPages - 2) tokens.push("ellipsis-right");
    tokens.push(totalPages);
    return tokens;
  }, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedStatusFilters,
    selectedTypeFilters,
    effectiveOfficialFilter,
    effectiveOrganizerFilterValue,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const searchPlaceholder =
    forcedTypeFilter === "tournament"
      ? siteTexts.challenges.publicList.searchPlaceholderTournament
      : effectiveOfficialFilter === "official"
        ? siteTexts.challenges.publicList.searchPlaceholderOfficial
        : siteTexts.challenges.publicList.searchPlaceholder;

  if (loading) {
    return <LoadingPage label={siteTexts.common.loading} />;
  }

  const showTypeFilterChips = !forcedTypeFilter;
  const allFiltersCleared =
    selectedStatusFilters.length === 0 &&
    (!showTypeFilterChips || selectedTypeFilters.length === 0);
  const activeFiltersCount =
    selectedStatusFilters.length +
    (showTypeFilterChips ? selectedTypeFilters.length : 0);
  const organizerPageStyle = hasForcedOrganizerUserId
    ? organizerIdentityStyle(organizerVisualIdentity)
    : undefined;

  return (
    <AppLayout>
      <section
        className={`challenges-showcase${
          forcedTypeFilter === "tournament"
            ? " challenges-showcase--tournaments"
            : ""
        }${hasForcedOrganizerUserId ? " challenges-showcase--organizer" : ""}`}
        style={organizerPageStyle}
      >
        <div className="challenges-showcase-hero">
          <div className="challenges-showcase-hero-top">
            <div className="challenges-showcase-hero-copy">
              <h1>{challengesPageTitle}</h1>
              <p>
                {forcedTypeFilter === "tournament"
                  ? siteTexts.challenges.publicList.subtitleTournament
                  : siteTexts.challenges.publicList.subtitle}
              </p>
            </div>
          </div>

          <div
            className={`challenges-showcase-filters${
              filtersOpen ? " is-open" : ""
            }`}
          >
            <div className="challenges-showcase-filters-row">
              <div className="challenges-showcase-search-wrap">
                <SearchInput
                  className="challenges-showcase-searchbar"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onClear={() => setSearchInput("")}
                  placeholder={searchPlaceholder}
                  aria-label={siteTexts.challenges.publicList.searchAriaLabel}
                />
              </div>

              <button
                type="button"
                className={`challenges-showcase-filter-toggle${
                  filtersOpen ? " is-active" : ""
                }`}
                aria-expanded={filtersOpen}
                aria-label={siteTexts.challenges.publicList.filtersAriaLabel}
                onClick={() => setFiltersOpen((current) => !current)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  tune
                </span>
                {activeFiltersCount > 0 && (
                  <span className="challenges-showcase-filter-count">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            <div className="challenges-showcase-filter-content">
              <div className="challenges-showcase-filter-group challenges-showcase-filter-group--status">
                <span className="challenges-showcase-filter-title">
                  {siteTexts.challenges.publicList.filterGroups.status}
                </span>
                <div className="challenges-showcase-pill-row" role="group">
                  <button
                    type="button"
                    className={`challenges-showcase-pill${
                      allFiltersCleared ? " is-active" : ""
                    }`}
                    onClick={() => {
                      setSelectedStatusFilters([]);
                      setSelectedTypeFilters(
                        forcedTypeFilter ? [forcedTypeFilter] : [],
                      );
                    }}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      grid_view
                    </span>
                    {siteTexts.challenges.publicList.filters.all}
                  </button>
                  <button
                    type="button"
                    className={`challenges-showcase-pill${
                      selectedStatusFilters.includes("pending")
                        ? " is-active"
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedStatusFilters((previous) =>
                        previous.includes("pending")
                          ? previous.filter((status) => status !== "pending")
                          : [...previous, "pending"],
                      )
                    }
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      event
                    </span>
                    {siteTexts.challenges.publicList.filters.pending}
                  </button>
                  <button
                    type="button"
                    className={`challenges-showcase-pill${
                      selectedStatusFilters.includes("started")
                        ? " is-active"
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedStatusFilters((previous) =>
                        previous.includes("started")
                          ? previous.filter((status) => status !== "started")
                          : [...previous, "started"],
                      )
                    }
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      play_circle
                    </span>
                    {siteTexts.challenges.publicList.filters.started}
                  </button>
                  <button
                    type="button"
                    className={`challenges-showcase-pill${
                      selectedStatusFilters.includes("finished")
                        ? " is-active"
                        : ""
                    }`}
                    onClick={() =>
                      setSelectedStatusFilters((previous) =>
                        previous.includes("finished")
                          ? previous.filter((status) => status !== "finished")
                          : [...previous, "finished"],
                      )
                    }
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      check_circle
                    </span>
                    {siteTexts.challenges.publicList.filters.finished}
                  </button>
                </div>
              </div>

              {showTypeFilterChips && (
                <div className="challenges-showcase-filter-group challenges-showcase-filter-group--type">
                  <span className="challenges-showcase-filter-title">
                    {siteTexts.challenges.publicList.filterGroups.type}
                  </span>
                  <div className="challenges-showcase-pill-row" role="group">
                    <button
                      type="button"
                      className={`challenges-showcase-pill${
                        selectedTypeFilters.includes("challenge")
                          ? " is-active"
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedTypeFilters((previous) =>
                          previous.includes("challenge")
                            ? previous.filter((type) => type !== "challenge")
                            : [...previous, "challenge"],
                        )
                      }
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        star
                      </span>
                      {siteTexts.challenges.publicList.filters.challenge}
                    </button>
                    <button
                      type="button"
                      className={`challenges-showcase-pill${
                        selectedTypeFilters.includes("super8")
                          ? " is-active"
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedTypeFilters((previous) =>
                          previous.includes("super8")
                            ? previous.filter((type) => type !== "super8")
                            : [...previous, "super8"],
                        )
                      }
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        groups
                      </span>
                      {siteTexts.challenges.publicList.filters.super8}
                    </button>
                    <button
                      type="button"
                      className={`challenges-showcase-pill${
                        selectedTypeFilters.includes("poco")
                          ? " is-active"
                          : ""
                      }`}
                      onClick={() =>
                        setSelectedTypeFilters((previous) =>
                          previous.includes("poco")
                            ? previous.filter((type) => type !== "poco")
                            : [...previous, "poco"],
                        )
                      }
                    >
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        emoji_events
                      </span>
                      {siteTexts.challenges.publicList.filters.poco}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {showOrganizerSelectorOnly && organizerOptions.length === 0 && (
          <p className="status-message">
            {siteTexts.challenges.publicList.labels.organizersEmpty}
          </p>
        )}
        {loadError && <p className="status-message">{loadError}</p>}

        <div className="challenges-showcase-grid">
          {paginatedItems.map((viewItem) => {
            const coverImage = resolveChallengeCoverImage(
              viewItem.source,
              content.events,
            );
            const participantsCount = Math.max(
              0,
              Number(viewItem.source.participantsCount || 0),
            );
            const slotsLimit = minParticipantsByType(viewItem.source);
            const progressPct = clamp(
              slotsLimit > 0 ? (participantsCount / slotsLimit) * 100 : 0,
              0,
              100,
            );
            const location =
              String(viewItem.source.address || "").trim() ||
              siteTexts.challenges.publicList.labels.locationFallback;
            const organizer =
              String(viewItem.source.organizerName || "").trim() ||
              siteTexts.challenges.publicList.labels.organizerFallback;
            const organizerAvatarUrl = String(
              viewItem.source.organizerAvatarUrl || "",
            ).trim();
            const organizerLogoUrl = String(
              viewItem.source.organizerLogoUrl || "",
            ).trim();
            const organizerSex = viewItem.source.organizerSex || "";
            const organizerIsTnp = isTnpOrganizer(organizer);
            const statusBadge = resolvePublicStatusBadge(
              String(viewItem.source.status || ""),
            );
            const typeBadge = viewItem.viewGroup
              ? `${siteTexts.challenges.publicList.labels.groupPrefix} ${viewItem.viewGroup}`
              : viewItem.source.challengeType === "super8" &&
                  isCombinedSuper8(viewItem.source) &&
                  viewItem.source.super8Group
                ? `${siteTexts.challenges.publicList.labels.groupPrefix} ${viewItem.source.super8Group}`
                : typeLabel(viewItem.source.challengeType);
            const isTournamentCard =
              String(viewItem.source.challengeType || "")
                .trim()
                .toLowerCase() === "tournament";
            const displayDateLabel = formatEventDateRangeLabel(
              viewItem.displayAt,
              viewItem.displayEndAt,
            );
            const slotsRemaining = remainingSlots(viewItem.source);
            const isFull = slotsLimit > 0 && participantsCount >= slotsLimit;
            const categoryText = categorySummary(viewItem.source);
            const categoryBadgeTone = categoryTone(viewItem.source);
            const tournamentFormat = tournamentFormatLabel(viewItem.source);
            const progressLabel = `${Math.round(progressPct)}%`;
            return (
              <a
                key={viewItem.viewKey}
                href={viewItem.detailHref}
                className={`challenge-showcase-card${
                  isTournamentCard ? " tournament-showcase-card" : ""
                }`}
                aria-label={`${siteTexts.challenges.publicList.labels.details}: ${viewItem.title}`}
              >
                <div
                  className={`challenge-showcase-card-media${
                    coverImage ? " has-image" : ""
                  }`}
                  style={
                    coverImage
                      ? ({
                          "--challenge-card-image": `url(${coverImage})`,
                        } as CSSProperties)
                      : undefined
                  }
                >
                  <div className="challenge-showcase-card-badges">
                    <span
                      className={`challenge-showcase-status-badge challenge-showcase-status-badge--${statusBadge.tone}`}
                    >
                      {statusBadge.label}
                    </span>
                    <div className="challenge-showcase-card-badges-right">
                      {Boolean(viewItem.source.includeInRanking) && (
                        <span
                          className="challenge-showcase-ranking-badge"
                          title={
                            siteTexts.challenges.publicList.labels
                              .rankingIncluded
                          }
                          aria-label={
                            siteTexts.challenges.publicList.labels
                              .rankingIncluded
                          }
                        >
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            emoji_events
                          </span>
                        </span>
                      )}
                      <span className="challenge-showcase-type-badge">
                        {isTournamentCard && (
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            emoji_events
                          </span>
                        )}
                        {typeBadge}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="challenge-showcase-card-body">
                  <h3>{viewItem.title}</h3>
                  <div className="challenge-showcase-meta-list">
                    {isTournamentCard ? (
                      <>
                        <p className="challenge-showcase-meta challenge-showcase-meta--date">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            calendar_month
                          </span>
                          {displayDateLabel ||
                            siteTexts.challenges.publicDetail.labels
                              .toBeDefined}
                        </p>
                        <p className="challenge-showcase-meta challenge-showcase-meta--location">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            location_on
                          </span>
                          {location}
                        </p>
                        {viewItem.distanceKm !== null && (
                          <p className="challenge-showcase-meta challenge-showcase-meta--distance">
                            <span
                              className="material-symbols-outlined"
                              aria-hidden="true"
                            >
                              near_me
                            </span>
                            {viewItem.distanceKm.toFixed(1)} km
                          </p>
                        )}
                        <p
                          className={`challenge-showcase-meta challenge-showcase-organizer-meta${
                            organizerIsTnp ? " has-tnp-logo" : ""
                          } challenge-showcase-meta--organizer`}
                        >
                          {organizerIsTnp ? (
                            <TnpOrganizerLogo size="compact" />
                          ) : organizerLogoUrl ? (
                            <img
                              className="challenge-showcase-organizer-logo"
                              src={organizerLogoUrl}
                              alt=""
                            />
                          ) : (
                            <>
                              <UserAvatar
                                className="challenge-showcase-organizer-avatar"
                                name={organizer}
                                sex={organizerSex}
                                avatarUrl={organizerAvatarUrl}
                                size={32}
                              />
                              {organizer}
                            </>
                          )}
                        </p>
                        <p className="challenge-showcase-meta challenge-showcase-meta--format">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            swords
                          </span>
                          {tournamentFormat}
                        </p>
                        <p className="challenge-showcase-meta challenge-showcase-meta--participants">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            groups
                          </span>
                          {participantsCount} / {slotsLimit} inscritos
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="challenge-showcase-meta">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            calendar_month
                          </span>
                          {displayDateLabel ||
                            siteTexts.challenges.publicDetail.labels
                              .toBeDefined}
                        </p>
                        <p className="challenge-showcase-meta">
                          <span
                            className="material-symbols-outlined"
                            aria-hidden="true"
                          >
                            location_on
                          </span>
                          {location}
                        </p>
                        <p
                          className={`challenge-showcase-meta challenge-showcase-organizer-meta${
                            organizerIsTnp ? " has-tnp-logo" : ""
                          }`}
                        >
                          {organizerIsTnp ? (
                            <TnpOrganizerLogo size="compact" />
                          ) : organizerLogoUrl ? (
                            <img
                              className="challenge-showcase-organizer-logo"
                              src={organizerLogoUrl}
                              alt=""
                            />
                          ) : (
                            <>
                              <UserAvatar
                                className="challenge-showcase-organizer-avatar"
                                name={organizer}
                                sex={organizerSex}
                                avatarUrl={organizerAvatarUrl}
                                size={32}
                              />
                              {organizer}
                            </>
                          )}
                        </p>
                      </>
                    )}
                  </div>
                  <p className="challenge-showcase-meta">
                    {displayDateLabel} • {location}
                  </p>
                  {!organizerIsTnp ? (
                    <p className="challenge-showcase-meta">
                      {siteTexts.challenges.publicList.labels.organizerPrefix}{" "}
                      {organizer}
                    </p>
                  ) : null}
                  {String(viewItem.source.challengeType || "")
                    .trim()
                    .toLowerCase() !== "tournament" && (
                    <p className="challenge-showcase-meta">
                      {categorySummary(viewItem.source)}
                    </p>
                  )}
                  {String(viewItem.source.challengeType || "")
                    .trim()
                    .toLowerCase() !== "tournament" && (
                    <span
                      className={`challenge-showcase-category-badge challenge-showcase-category-badge--${categoryBadgeTone}`}
                    >
                      {categoryText}
                    </span>
                  )}

                  <div className="challenge-showcase-progress-wrap">
                    {isTournamentCard ? (
                      <div className="challenge-showcase-progress-copy challenge-showcase-progress-copy--tournament">
                        <strong>{siteTexts.challenges.publicList.labels.registrations}</strong>
                        <span
                          className={
                            isFull
                              ? "challenge-showcase-percent challenge-showcase-percent--full"
                              : "challenge-showcase-percent"
                          }
                        >
                          {progressLabel}
                        </span>
                      </div>
                    ) : (
                      <div className="challenge-showcase-progress-copy">
                        <strong>
                          {participantsCount} / {slotsLimit}{" "}
                          {
                            siteTexts.challenges.publicList.labels
                              .registeredSuffix
                          }
                        </strong>
                        <span
                          className={
                            isFull
                              ? "challenge-showcase-slots challenge-showcase-slots--full"
                              : "challenge-showcase-slots"
                          }
                        >
                          {isFull
                            ? "LOTADO"
                            : `${slotsRemaining} vagas restantes`}
                        </span>
                      </div>
                    )}
                    <strong>
                      {participantsCount} / {slotsLimit}{" "}
                      {siteTexts.challenges.publicList.labels.registeredSuffix}
                    </strong>
                    <div
                      className="challenge-showcase-progress-track"
                      aria-hidden="true"
                    >
                      <span
                        className={
                          isFull
                            ? "challenge-showcase-progress-fill challenge-showcase-progress-fill--full"
                            : "challenge-showcase-progress-fill"
                        }
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    {isTournamentCard && (
                      <span className="challenge-showcase-progress-detail">
                        {participantsCount} de {slotsLimit} vagas preenchidas
                      </span>
                    )}
                  </div>
                </div>
              </a>
            );
          })}
          {totalItems === 0 && (
            <EmptyState
              title={forcedTypeFilter === "tournament"
                ? siteTexts.challenges.publicList.labels.emptyTournament
                : siteTexts.challenges.publicList.labels.empty}
              iconName={forcedTypeFilter === "tournament" ? "emoji_events" : "sports_tennis"}
            />
          )}
        </div>

        {totalItems > 0 && (
          <div className="challenges-showcase-footer">
            <p className="challenges-showcase-count">
              {forcedTypeFilter === "tournament" ? (
                <>
                  Mostrando {startItem}-{endItem} de {totalItems} torneios
                </>
              ) : (
                <>
                  {siteTexts.challenges.publicList.labels.count} {startItem}{" "}
                  {siteTexts.challenges.publicList.labels.countFromTo}{" "}
                  {endItem} {siteTexts.challenges.publicList.labels.countOf}{" "}
                  {totalItems} {siteTexts.challenges.publicList.labels.countItems}
                </>
              )}
            </p>

            <PageSizeSelector
              className="challenges-showcase-page-size"
              value={itemsPerPageValue}
              onChange={(value) => {
                setItemsPerPageValue(value);
                setCurrentPage(1);
              }}
            />

            <div className="challenges-showcase-pagination">
              <button
                type="button"
                className="challenges-showcase-page-btn"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage <= 1}
                aria-label={siteTexts.challenges.publicList.paging.prevAria}
              >
                ‹
              </button>
              {paginationTokens.map((tokenItem) =>
                typeof tokenItem === "number" ? (
                  <button
                    key={`page-${tokenItem}`}
                    type="button"
                    className={`challenges-showcase-page-btn${
                      currentPage === tokenItem ? " active" : ""
                    }`}
                    onClick={() => setCurrentPage(tokenItem)}
                  >
                    {tokenItem}
                  </button>
                ) : (
                  <span
                    key={tokenItem}
                    className="challenges-showcase-page-ellipsis"
                    aria-hidden="true"
                  >
                    …
                  </span>
                ),
              )}
              <button
                type="button"
                className="challenges-showcase-page-btn"
                onClick={() =>
                  setCurrentPage((page) => Math.min(totalPages, page + 1))
                }
                disabled={currentPage >= totalPages}
                aria-label={siteTexts.challenges.publicList.paging.nextAria}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </section>
    </AppLayout>
  );
}
